import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Layers, ClipboardCheck, Calendar, Tag } from "lucide-react";
import { heroTrustBadges } from "../mock";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80";

const badgeIconMap = { ShieldCheck, Award, Layers, ClipboardCheck };

export default function Hero() {
  return (
    <section className="relative isolate bg-[#06182C] text-white overflow-hidden" data-testid="home-hero">
      {/* Full background — solid premium navy */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#06182C] via-[#0A2540] to-[#0F2F4F]" />

      {/* RIGHT side image — flows seamlessly */}
      <div className="absolute inset-y-0 right-0 w-[58%] lg:w-[52%] hidden lg:block">
        <img
          src={HERO_IMAGE}
          alt="Senior accredited NDIS compliance consultants reviewing audit documentation"
          className="w-full h-full object-cover"
        />
        {/* Strong navy fade on the left edge of the image so it blends into the hero text area */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#06182C] via-[#06182C]/55 to-[#06182C]/20" />
        {/* Slight navy/teal tint over entire image for cohesion */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#06182C]/45 via-[#0A2540]/15 to-[#0F766E]/15" />
      </div>

      <div className="container-x relative z-10 pt-20 md:pt-28 lg:pt-32 pb-0">
        {/* TOP — content + visual */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center pb-16 md:pb-20 lg:pb-24">
          <div className="lg:col-span-7 fade-in-up">
            <h1
              className="text-white font-bold tracking-tight leading-[1.02] text-5xl md:text-6xl lg:text-[80px]"
              data-testid="hero-heading"
            >
              Become a<br />
              <span className="text-teal-light">Registered</span>
              <br />
              NDIS Provider
            </h1>

            <p
              className="mt-7 text-slate-200/90 text-base md:text-[17px] max-w-xl leading-relaxed"
              data-testid="hero-subheading"
            >
              Practical registration, compliance and audit readiness support informed by senior
              experienced accredited auditors with real understanding of NDIS audit expectations and
              provider implementation requirements.
            </p>

            <div className="mt-9 flex flex-wrap gap-3.5">
              <Link
                to="/contact"
                className="btn-primary px-6 py-3.5 rounded-xl font-semibold text-base inline-flex items-center gap-2.5 shadow-lg shadow-teal/20 hover:shadow-xl hover:shadow-teal/30 transition-shadow"
                data-testid="hero-primary-cta"
              >
                <Calendar size={18} /> Book Discovery Call
              </Link>
              <Link
                to="/pricing"
                className="bg-transparent hover:bg-white/8 border border-white/40 text-white px-6 py-3.5 rounded-xl font-semibold text-base inline-flex items-center gap-2.5 transition"
                data-testid="hero-secondary-cta"
              >
                <Tag size={17} /> View Pricing
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5" aria-hidden="true">
            {/* Image is rendered in the absolute right pane above. This column reserves layout space. */}
          </div>
        </div>

        {/* TRUST BADGES — integrated into the hero, separated by thin vertical lines */}
        <div
          className="relative border-t border-white/10 pt-10 pb-14 md:pb-20"
          data-testid="home-trust-badges"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-0 lg:divide-x lg:divide-white/10">
            {heroTrustBadges.map((b) => {
              const Icon = badgeIconMap[b.icon] || ShieldCheck;
              return (
                <div
                  key={b.title}
                  className="flex gap-5 px-0 lg:px-8 first:lg:pl-0 last:lg:pr-0"
                  data-testid={`trust-badge-${b.icon.toLowerCase()}`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-white/5 border border-teal-mid/30 text-teal-light flex items-center justify-center shrink-0">
                    <Icon size={28} strokeWidth={1.7} />
                  </div>
                  <div className="pt-1">
                    <h3 className="text-white font-bold text-[17px] leading-snug">{b.title}</h3>
                    <p className="mt-2.5 text-[14.5px] text-slate-300/90 leading-[1.65]">{b.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
